import React, { useEffect, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'query-string';
import {
  Row,
  Col,
  Button,
  Spinner,
  Collapse,
  Alert,
  Skiplink,
  SkiplinkItem,
} from 'design-react-kit';
import cx from 'classnames';

import { TextFilter } from 'design-comuni-plone-theme/components/ItaliaTheme/Blocks/Common/SearchFilters';
import SelectFilter from 'design-comuni-plone-theme/components/ItaliaTheme/Blocks/Common/SearchFilters/SelectFilter';
import { SkipToMainContent } from 'design-comuni-plone-theme/components/ItaliaTheme/View';
import { SelectInput } from 'design-comuni-plone-theme/components';
import {
  Pagination,
  FontAwesomeIcon,
} from 'design-comuni-plone-theme/components/ItaliaTheme';
// import { useDebouncedEffect } from 'design-comuni-plone-theme/helpers';
import { solrSearch, resetSolrSearch } from 'parmasottositi-theme/actions';
import SearchDocTypes from './SearchDocTypes';
import SearchDomains from './SearchDomains';
import { getSearchParamsURL, DEFAULT_PAGE_SIZE } from './utils';

import config from '@plone/volto/registry';

const messages = defineMessages({
  find: {
    id: 'find',
    defaultMessage: 'Cerca',
  },
  parma_search_keyword: {
    id: 'parma_search_keyword',
    defaultMessage: 'Digita la parola chiave',
  },
  parma_search_all: {
    id: 'parma_search_all',
    defaultMessage: 'Tutte le sezioni',
  },
  parma_search_services: {
    id: 'parma_search_services',
    defaultMessage: 'Servizi',
  },
  parma_search_news: {
    id: 'parma_search_news',
    defaultMessage: 'Novità',
  },
  parma_search_sections_placeholder: {
    id: 'parma_search_sections_placeholder',
    defaultMessage: 'Sezioni',
  },
  orderBy: {
    id: 'order_by',
    defaultMessage: 'Ordina per',
  },
  sort_on_date: {
    id: 'sort_on_date',
    defaultMessage: 'Data (prima i più recenti)',
  },
  sort_on_relevance: {
    id: 'sort_on_relevance',
    defaultMessage: 'Rilevanza',
  },
  sort_on_title: {
    id: 'sort_on_title',
    defaultMessage: 'Alfabeticamente',
  },
  foundNResults: {
    id: 'found_n_results',
    defaultMessage: 'Trovati {total} risultati.',
  },
  attenzione: { id: 'Attenzione!', defaultMessage: 'Attenzione!' },
  errors_occured: {
    id: 'Sono occorsi degli errori',
    defaultMessage: 'Sono occorsi degli errori',
  },
  no_results: {
    id: 'Nessun risultato ottenuto',
    defaultMessage: 'Nessun risultato ottenuto',
  },
  parma_specific_searches: {
    id: 'parma_specific_searches',
    defaultMessage: 'Ricerche specifiche',
  },
  parma_search_in_services: {
    id: 'parma_search_in_services',
    defaultMessage: 'Cerca in Servizi',
  },
  parma_search_in_news: {
    id: 'parma_search_in_news',
    defaultMessage: 'Cerca in Novità',
  },
  parma_filter_doc_type: {
    id: 'parma_filter_doc_type',
    defaultMessage: 'Tipo di documento',
  },
  parma_filter_domain: {
    id: 'parma_filter_domain',
    defaultMessage: 'Dominio',
  },
  skipToSearchResults: {
    id: 'search_skip_to_search_results',
    defaultMessage: 'Vai ai risultati di ricerca',
  },
  filtersCollapse: {
    id: 'filtersCollapse',
    defaultMessage: 'Filtri',
  },
});

const searchOrderDict = {
  relevance: 'relevance',
  date: 'date',
  sortable_title: 'alphabetical',
};

const SearchHomepage = ({ content }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const parsedQs = qs.parse(location.search) ?? {};

  const [searchableText, setSearchableText] = useState(
    parsedQs.SearchableText ?? '',
  );
  const [section, setSection] = useState({
    value: 'all',
    label: intl.formatMessage(messages.parma_search_all),
  });
  const [docTypesFilter, setDocTypesFilter] = useState(
    parsedQs.selected_facet_type ?? '',
  );
  const [domainsFilter, setDomainsFilter] = useState(
    parsedQs.selected_facet_domain ?? '',
  );
  const [sortOn, setSortOn] = useState(parsedQs.sorting ?? 'relevance');
  const [currentPage, setCurrentPage] = useState(parsedQs.actual_page ?? 1);
  const [collapseFilters, _setCollapseFilters] = useState(true);

  const searchResults = useSelector((state) => state.solrSearch);

  const setCollapseFilters = (collapse) => {
    _setCollapseFilters(collapse);
    if (window?.innerWidth <= 991 && collapse)
      setTimeout(
        () =>
          document
            .querySelector('main')
            ?.scrollIntoView?.({ behavior: 'smooth' }),
        100,
      );
  };

  const sortOnOptions = [
    {
      value: 'relevance',
      label: intl.formatMessage(messages.sort_on_relevance),
    },
    {
      value: 'date',
      label: intl.formatMessage(messages.sort_on_date),
    },
    {
      value: 'sortable_title',
      label: intl.formatMessage(messages.sort_on_title),
    },
  ];

  const handleQueryPaginationChange = (_e, { activePage }) => {
    window.scrollTo(0, 0);
    setCurrentPage(activePage?.children ?? 1);
  };

  // useDebouncedEffect(
  useEffect(
    () => {
      doSearch();
    },
    // 600,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      // searchableText,
      docTypesFilter,
      domainsFilter,
      sortOn,
      currentPage,
    ],
  );

  useEffect(() => {
    return () => {
      dispatch(resetSolrSearch());
    };
  }, [dispatch]);

  const doSearch = () => {
    const queryString = getSearchParamsURL(
      searchableText?.length > 0 ? `${searchableText}*` : '',
      searchOrderDict[sortOn] ?? 'relevance',
      currentPage,
      docTypesFilter,
      domainsFilter,
      true,
    );
    searchResults.result &&
      history.push(
        getSearchParamsURL(
          searchableText,
          searchOrderDict[sortOn] ?? 'relevance',
          currentPage,
          docTypesFilter,
          domainsFilter,
        ),
      );
    dispatch(solrSearch(queryString));
  };

  const locale = config.settings.supportedLanguages?.includes(intl.locale)
    ? intl.locale
    : 'it';

  let mainSiteUrl =
    (__CLIENT__ && window.env.RAZZLE_MAIN_SITE_URL) ||
    process.env.RAZZLE_MAIN_SITE_URL ||
    '';
  if (mainSiteUrl && mainSiteUrl[mainSiteUrl.length - 1] !== '/') {
    mainSiteUrl = mainSiteUrl + '/';
  }

  return (
    <>
      <div className="container px-4 my-4 search-homepage-view">
        <SkipToMainContent />
        <header className="search-homepage-header">
          <h1 className="mt-5 mb-4">{content.title}</h1>
          {content.description && (
            <p className="mb-5 description">{content.description}</p>
          )}
          <div
            className={cx('rounded bg-primary search-block-filters', {
              'has-results': searchResults.result?.data?.length > 0,
              'no-results': !(searchResults.result?.data?.length > 0),
            })}
          >
            <div className="d-flex justify-content-center">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (section.value === 'all') {
                    doSearch();
                  } else {
                    window.location.href = `${mainSiteUrl}${locale}/search?${qs.stringify(
                      {
                        SearchableText: searchableText,
                        'path.query': `/${section.value}`,
                      },
                    )}`;
                  }
                }}
                className="d-flex search-container align-items-center justify-content-center flex-wrap"
              >
                <TextFilter
                  id="filterOne"
                  value={searchableText}
                  placeholder={intl.formatMessage(
                    messages.parma_search_keyword,
                  )}
                  onChange={(filterId, value) => {
                    setSearchableText(value);
                  }}
                />
                <SelectFilter
                  id="filterTwo"
                  value={section}
                  options={{
                    choices: [
                      {
                        value: 'all',
                        label: intl.formatMessage(messages.parma_search_all),
                      },
                      {
                        value: 'servizi',
                        label: intl.formatMessage(
                          messages.parma_search_services,
                        ),
                      },
                      {
                        value: 'novita',
                        label: intl.formatMessage(messages.parma_search_news),
                      },
                    ],
                    placeholder: intl.formatMessage(
                      messages.parma_search_sections_placeholder,
                    ),
                    isClearable: false,
                  }}
                  onChange={(filterId, value) => {
                    setSection(value);
                  }}
                />
                <Button
                  type="submit"
                  color="tertiary"
                  icon={false}
                  tag="button"
                  className="my-2 my-lg-1"
                >
                  {intl.formatMessage(messages.find)}
                </Button>
              </form>
            </div>
          </div>
          {searchResults.result?.data?.length > 0 && (
            <>
              <Skiplink tag="div">
                <SkiplinkItem href="#search-results-region" tag="a">
                  {intl.formatMessage(messages.skipToSearchResults)}
                </SkiplinkItem>
              </Skiplink>
              <div className="d-block d-lg-none d-xl-none">
                <div className="row pb-3">
                  <div className="col-12 mb-3">
                    <small>
                      {intl.formatMessage(messages.foundNResults, {
                        total: searchResults.result.resultnumber,
                      })}
                    </small>
                  </div>
                  <div className="col-12 align-self-center d-flex align-items-center">
                    <div className="me-5">
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          setCollapseFilters((prev) => !prev);
                        }}
                        href="#categoryCollapse"
                        role="button"
                        className={cx('btn btn-sm fw-bold text-uppercase', {
                          'btn-outline-primary': collapseFilters,
                          'btn-primary': !collapseFilters,
                        })}
                        data-toggle="collapse"
                        aria-expanded={!collapseFilters}
                        aria-controls="categoryCollapse"
                      >
                        {intl.formatMessage(messages.filtersCollapse)}
                      </a>
                    </div>
                    <p className="mb-0 text-end flex-shrink-0">
                      {intl.formatMessage(messages.orderBy)}
                    </p>
                    <div className="sort-on-mobile w-100">
                      <SelectInput
                        id="search-sort-on-mobile"
                        value={
                          sortOnOptions.filter((o) => o.value === sortOn)[0]
                        }
                        placeholder={intl.formatMessage(messages.orderBy)}
                        onChange={(opt) => setSortOn(opt.value)}
                        options={sortOnOptions}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </header>

        {searchResults.result?.data?.length > 0 && (
          <Row>
            <aside className="col-lg-3 py-lg-5">
              <div className="pe-4"></div>
              <Collapse
                isOpen={!collapseFilters}
                className="d-lg-block d-xl-block"
                id="categoryCollapse"
              >
                <div className="pt-4 pt-lg-0 mb-lg-5 specific-searches d-none d-lg-block">
                  <h6 className="text-uppercase">
                    {intl.formatMessage(messages.parma_specific_searches)}
                  </h6>
                  <div className="mt-2">
                    <Button
                      color="tertiary"
                      tag="a"
                      href={`${mainSiteUrl}${locale}/search?${qs.stringify({
                        SearchableText: searchableText,
                        'path.query': '/servizi',
                      })}`}
                    >
                      {intl.formatMessage(messages.parma_search_in_services)}
                      <FontAwesomeIcon icon={['fas', 'external-link-alt']} />
                    </Button>
                  </div>
                  <div className="mt-2">
                    <Button
                      color="tertiary"
                      tag="a"
                      href={`${mainSiteUrl}${locale}/search?${qs.stringify({
                        SearchableText: searchableText,
                        'path.query': '/novita',
                      })}`}
                    >
                      {intl.formatMessage(messages.parma_search_in_news)}
                      <FontAwesomeIcon icon={['fas', 'external-link-alt']} />
                    </Button>
                  </div>
                </div>

                {Object.keys(searchResults.result.facet_fields.host)?.length >
                  0 && (
                  <div className="pt-4 pt-lg-0 mb-lg-5">
                    <h6 className="text-uppercase">
                      {intl.formatMessage(messages.parma_filter_domain)}
                    </h6>
                    <Row className="form-check mt-4">
                      <SearchDomains
                        domains={searchResults.result.facet_fields.host}
                        domainsFilter={domainsFilter}
                        setDomainsFilter={setDomainsFilter}
                      />
                    </Row>
                  </div>
                )}

                {Object.keys(searchResults.result.facet_fields.type)?.length >
                  0 && (
                  <div className="pt-4 pt-lg-0 mb-lg-5">
                    <h6 className="text-uppercase">
                      {intl.formatMessage(messages.parma_filter_doc_type)}
                    </h6>
                    <Row className="form-check mt-4">
                      <SearchDocTypes
                        docTypes={searchResults.result.facet_fields.type}
                        docTypesFilter={docTypesFilter}
                        setDocTypesFilter={setDocTypesFilter}
                      />
                    </Row>
                  </div>
                )}
              </Collapse>
            </aside>

            <Col lg={9} tag="section" className="py-lg-5">
              {searchResults.loading ? (
                <Spinner active />
              ) : searchResults?.result?.resultnumber > 0 ? (
                <div
                  className="search-results-wrapper"
                  role="region"
                  id="search-results-region"
                  aria-live="polite"
                >
                  <div className="d-none d-lg-block ordering-widget">
                    <Row className="pb-3">
                      <Col xs={6} className="align-self-center">
                        <p>
                          {intl.formatMessage(messages.foundNResults, {
                            total: searchResults.result.resultnumber,
                          })}
                        </p>
                      </Col>
                      <Col xs={6}>
                        <SelectInput
                          id="search-sort-on"
                          value={
                            sortOnOptions.filter((o) => o.value === sortOn)[0]
                          }
                          label={intl.formatMessage(messages.orderBy)}
                          placeholder={intl.formatMessage(messages.orderBy)}
                          onChange={(opt) => setSortOn(opt.value)}
                          options={sortOnOptions}
                        />
                      </Col>
                    </Row>
                  </div>
                  <Row className="search-results">
                    {searchResults.result.data.map((item) => (
                      <Col sm={12} key={item.id}>
                        <div className="mt-3 mb-2 pb-4 result-item">
                          <div className="mb-2 ellipsis">
                            <a
                              className="link-breadcrumb"
                              href={item.url[0]}
                              title={item.title?.[0] ?? item.url[0]}
                            >
                              {item.url[0]}
                            </a>
                          </div>
                          <div className="title h3 mb-3">
                            <a href={item.url[0]}>
                              {item.title?.[0] ?? item.url[0]}
                            </a>
                          </div>
                          <div
                            className="item-description"
                            dangerouslySetInnerHTML={{ __html: item.highlight }}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                  {searchResults.result.resultnumber > DEFAULT_PAGE_SIZE && (
                    <Pagination
                      activePage={currentPage}
                      totalPages={Math.ceil(
                        searchResults.result.resultnumber / DEFAULT_PAGE_SIZE,
                      )}
                      onPageChange={handleQueryPaginationChange}
                    />
                  )}
                </div>
              ) : searchResults.error ? (
                <Alert color="danger">
                  <strong>{intl.formatMessage(messages.attenzione)}</strong>{' '}
                  {intl.formatMessage(messages.errors_occured)}
                </Alert>
              ) : (
                <p>{intl.formatMessage(messages.no_results)}</p>
              )}
            </Col>
          </Row>
        )}
      </div>
    </>
  );
};

export default SearchHomepage;
